import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, User as UserIcon, Sparkles, Mail, UserPlus, Phone, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { playSignInChime } from "@/lib/chime";
import ForgotPasswordModal from "@/components/site/ForgotPasswordModal";
import PasswordInput from "@/components/site/PasswordInput";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const buildGoogleAuthUrl = (clientId) => {
  const redirectUri = window.location.origin + "/auth/google";
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    prompt: "select_account",
    include_granted_scopes: "true",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return detail.msg || String(detail);
};

const SIGNUP_STEP = { DETAILS: 0, VERIFY: 1, PASSWORD: 2 };

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [devMode, setDevMode] = useState(false);
  const [demoBusy, setDemoBusy] = useState("");
  const [tab, setTab] = useState("signin"); // signin | signup
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  // Sign-in form
  const [signin, setSignin] = useState({ email: "", password: "" });
  const [showForgot, setShowForgot] = useState(false);

  // Multi-step Sign-up state
  const [signupStep, setSignupStep] = useState(SIGNUP_STEP.DETAILS);
  const [signup, setSignup] = useState({ name: "", email: "", phone: "", password: "", code: "" });
  const [otpToken, setOtpToken] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/google/config"); setConfig(data); } catch { setConfig({ enabled: false }); }
      try { const { data } = await api.get("/dev/status"); setDevMode(!!data.dev_mode); } catch { setDevMode(false); }
    })();
  }, []);

  useEffect(() => {
    if (!loading && user) {
      const next = location.state?.next || "/";
      navigate(next, { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  const handleGoogle = () => {
    if (!config?.client_id) return;
    window.location.href = buildGoogleAuthUrl(config.client_id);
  };

  const demoLogin = async (role) => {
    setDemoBusy(role);
    try {
      await api.post("/dev/login-as", { role }, { withCredentials: true });
      playSignInChime();
      toast.success(`Signed in as demo ${role}.`);
      if (refresh) await refresh();
      navigate(role === "admin" ? "/admin" : "/", { replace: true });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setDemoBusy("");
    }
  };

  const submitSignIn = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        "/auth/login",
        { email: signin.email, password: signin.password },
        { withCredentials: true },
      );
      playSignInChime();
      toast.success("Welcome back!");
      if (refresh) await refresh();
      const next = data.is_admin ? "/admin" : (location.state?.next || "/");
      navigate(next, { replace: true });
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---- Sign-up: step 1 — send OTP ----
  const sendOtp = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      if (!termsAccepted) {
        setError("Please accept the Terms & Privacy Policy to continue.");
        setBusy(false);
        return;
      }
      // Quick client-side phone length check before round-trip
      const digits = (signup.phone || "").replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 15) {
        setError("Phone must be 10–15 digits.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/auth/otp/send", { email: signup.email, purpose: "register" });
      if (data?.dev_code) setDevOtp(data.dev_code);
      setSignupStep(SIGNUP_STEP.VERIFY);
      toast.success("Verification code sent.");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---- Sign-up: step 2 — verify OTP ----
  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/verify", { email: signup.email, code: signup.code, purpose: "register" });
      setOtpToken(data.otp_token);
      setSignupStep(SIGNUP_STEP.PASSWORD);
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const resendOtp = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/send", { email: signup.email, purpose: "register" });
      if (data?.dev_code) setDevOtp(data.dev_code);
      toast.success("Code re-sent.");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---- Sign-up: step 3 — set password + create account ----
  const createAccount = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/register", {
        email: signup.email,
        password: signup.password,
        name: signup.name,
        phone: signup.phone,
        otp_token: otpToken,
        terms_accepted: termsAccepted,
        marketing_opt_in: marketingOptIn,
      }, { withCredentials: true });
      playSignInChime();
      toast.success("Account created.");
      if (refresh) await refresh();
      navigate(location.state?.next || "/", { replace: true });
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const switchTab = (next) => { setTab(next); setError(""); setSignupStep(SIGNUP_STEP.DETAILS); };

  return (
    <div className="min-h-[80vh] bg-black flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="w-full max-w-md bg-spike-elevated border border-white/10 rounded-2xl p-8 shadow-card-hover">
        <div className="inline-flex items-center gap-2 text-xs font-orbitron font-bold text-spike-blue-light tracking-[0.3em] uppercase">
          <ShieldCheck className="w-3.5 h-3.5"/> {tab === "signin" ? "Sign In" : "Sign Up"}
        </div>
        <h1 className="mt-3 font-orbitron text-3xl font-black uppercase text-spike-text leading-tight">
          {tab === "signin" ? <>Welcome <span className="text-spike-blue-light">Back.</span></> : <>Join <span className="text-spike-blue-light">Spike.</span></>}
        </h1>
        <p className="mt-3 text-sm text-spike-text-muted leading-relaxed font-exo">
          {tab === "signin" ? "Access bookings, memberships and your signed waiver." : "Create your account in a few steps to book and sign your waiver."}
        </p>

        {/* Tabs */}
        <div className="mt-6 grid grid-cols-2 gap-1 p-1 bg-black/60 border border-white/10 rounded-md">
          <button type="button" data-testid="auth-tab-signin" onClick={() => switchTab("signin")} className={`px-3 py-2 text-xs font-orbitron font-bold uppercase tracking-[0.2em] rounded-sm transition-colors ${tab === "signin" ? "bg-spike-blue text-black" : "text-spike-text-muted hover:text-spike-text"}`}>Sign In</button>
          <button type="button" data-testid="auth-tab-signup" onClick={() => switchTab("signup")} className={`px-3 py-2 text-xs font-orbitron font-bold uppercase tracking-[0.2em] rounded-sm transition-colors ${tab === "signup" ? "bg-spike-blue text-black" : "text-spike-text-muted hover:text-spike-text"}`}>Sign Up</button>
        </div>

        {error && <p data-testid="login-error" className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</p>}

        {/* ---------- SIGN IN ---------- */}
        {tab === "signin" && (
          <form onSubmit={submitSignIn} className="mt-5 space-y-3" data-testid="signin-form">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
              <input type="email" required placeholder="you@example.com" data-testid="signin-email" value={signin.email} onChange={(e) => setSignin({ ...signin, email: e.target.value })} className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"/>
            </div>
            <PasswordInput
              required
              minLength={1}
              placeholder="Password"
              data-testid="signin-password"
              value={signin.password}
              onChange={(e) => setSignin({ ...signin, password: e.target.value })}
            />
            <div className="flex justify-end">
              <button type="button" data-testid="forgot-link" onClick={() => setShowForgot(true)} className="text-xs text-spike-blue-light hover:underline font-orbitron uppercase tracking-[0.15em]">Forgot password?</button>
            </div>
            <button type="submit" disabled={busy} data-testid="signin-submit" className="w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Sign In"}
            </button>
          </form>
        )}

        {/* ---------- SIGN UP — step 1: details ---------- */}
        {tab === "signup" && signupStep === SIGNUP_STEP.DETAILS && (
          <form onSubmit={sendOtp} className="mt-5 space-y-3" data-testid="signup-form-details">
            <p className="text-xs text-spike-dim font-orbitron uppercase tracking-[0.2em]">Step 1 of 3 · Your details</p>
            <div className="relative">
              <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
              <input type="text" required minLength={2} placeholder="Full name" data-testid="signup-name" value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"/>
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
              <input type="email" required placeholder="you@example.com" data-testid="signup-email" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"/>
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
              <input type="tel" required placeholder="Phone (required) e.g. 647-555-0100" data-testid="signup-phone" value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"/>
            </div>

            <div className="pt-1 space-y-2.5">
              <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="signup-terms-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  data-testid="signup-terms-checkbox"
                  className="mt-0.5 w-4 h-4 accent-spike-blue cursor-pointer flex-shrink-0"
                  required
                />
                <span className="text-xs text-spike-text-muted leading-relaxed font-exo">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-spike-blue-light underline" data-testid="signup-terms-link">Terms &amp; Conditions</Link>{" "}and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-spike-blue-light underline" data-testid="signup-privacy-link">Privacy Policy</Link>. <span className="text-red-400">*</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="signup-marketing-label">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  data-testid="signup-marketing-checkbox"
                  className="mt-0.5 w-4 h-4 accent-spike-blue cursor-pointer flex-shrink-0"
                />
                <span className="text-xs text-spike-text-muted leading-relaxed font-exo">
                  Send me drop-in deals, training tips and Spike Sports updates. Unsubscribe any time.
                </span>
              </label>
            </div>

            <button type="submit" disabled={busy || !termsAccepted} data-testid="signup-next-btn" className="w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 disabled:cursor-not-allowed text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send Verification Code"}
            </button>
          </form>
        )}

        {/* ---------- SIGN UP — step 2: verify email ---------- */}
        {tab === "signup" && signupStep === SIGNUP_STEP.VERIFY && (
          <form onSubmit={verifyOtp} className="mt-5 space-y-3" data-testid="signup-form-verify">
            <button type="button" onClick={() => setSignupStep(SIGNUP_STEP.DETAILS)} className="text-xs text-spike-dim hover:text-spike-text inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3"/> Back</button>
            <p className="text-xs text-spike-dim font-orbitron uppercase tracking-[0.2em]">Step 2 of 3 · Verify email</p>
            <p className="text-sm text-spike-text-muted font-exo">
              We sent a 6-digit code to <strong className="text-spike-text">{signup.email}</strong>. It expires in 10 minutes.
            </p>
            {devOtp && (
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="signup-dev-code">
                DEV preview only — code: <strong className="font-mono">{devOtp}</strong>
              </p>
            )}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              maxLength={6}
              placeholder="6-digit code"
              data-testid="signup-otp-input"
              value={signup.code}
              onChange={(e) => setSignup({ ...signup, code: e.target.value.replace(/\D/g, "") })}
              className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2.5 text-base text-spike-text tracking-[0.6em] font-mono text-center focus:outline-none focus:border-spike-blue"
            />
            <button type="submit" disabled={busy || signup.code.length !== 6} data-testid="signup-verify-btn" className="w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Verify Code"}
            </button>
            <button type="button" onClick={resendOtp} disabled={busy} data-testid="signup-resend-btn" className="w-full text-xs text-spike-blue-light hover:underline font-orbitron uppercase tracking-[0.15em]">Resend code</button>
          </form>
        )}

        {/* ---------- SIGN UP — step 3: password ---------- */}
        {tab === "signup" && signupStep === SIGNUP_STEP.PASSWORD && (
          <form onSubmit={createAccount} className="mt-5 space-y-3" data-testid="signup-form-password">
            <p className="text-xs text-spike-dim font-orbitron uppercase tracking-[0.2em]">Step 3 of 3 · Choose password</p>
            <PasswordInput
              required
              minLength={8}
              placeholder="At least 8 characters"
              data-testid="signup-password"
              value={signup.password}
              onChange={(e) => setSignup({ ...signup, password: e.target.value })}
            />
            <button type="submit" disabled={busy} data-testid="signup-finish-btn" className="w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create Account"}
            </button>
          </form>
        )}

        {/* Google OAuth */}
        {config?.enabled && (
          <>
            <div className="my-5 flex items-center gap-3 text-spike-dim text-xs">
              <div className="h-px bg-white/10 flex-1"/> OR <div className="h-px bg-white/10 flex-1"/>
            </div>
            <button type="button" onClick={handleGoogle} data-testid="login-google-btn" className="w-full inline-flex items-center justify-center gap-3 bg-white hover:bg-white/90 text-black font-semibold text-sm px-6 py-3 rounded-md transition-colors">
              <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20.4H24v7.2h11.3c-1.5 4.2-5.5 7.2-11.3 7.2-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.6 5.8 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.2-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l5.9 4.3C13.8 15.2 18.5 12 24 12c3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.6 5.8 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-5.9-5c-1.9 1.3-4.4 2-7 2-5.7 0-10.5-3.6-12.2-8.6l-5.9 4.6C9.2 39.4 16 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20.4H24v7.2h11.3c-.7 2-2 3.7-3.6 5l5.9 5c4.2-3.9 6.4-9.6 6.4-15.7 0-1.3-.2-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
          </>
        )}

        {devMode && (
          <div className="mt-6 pt-6 border-t border-dashed border-white/15" data-testid="demo-login-section">
            <div className="flex items-center gap-2 text-xs font-orbitron font-bold text-spike-blue-light uppercase tracking-[0.3em] mb-3">
              <Sparkles className="w-3 h-3"/> Demo · one-click
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <button type="button" data-testid="demo-login-admin" disabled={!!demoBusy} onClick={() => demoLogin("admin")} className="inline-flex items-center justify-center gap-2 bg-spike-blue/15 border border-spike-blue/40 hover:bg-spike-blue/25 text-spike-blue-light font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-4 py-2.5 rounded-sm">
                {demoBusy === "admin" ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>} Demo Admin
              </button>
              <button type="button" data-testid="demo-login-player" disabled={!!demoBusy} onClick={() => demoLogin("player")} className="inline-flex items-center justify-center gap-2 bg-spike-blue/15 border border-spike-blue/40 hover:bg-spike-blue/25 text-spike-blue-light font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-4 py-2.5 rounded-sm">
                {demoBusy === "player" ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserIcon className="w-4 h-4"/>} Demo Player
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-xs text-spike-dim text-center font-exo">
          {tab === "signin" ? (
            <>By signing in you agree to our{" "}
              <Link to="/terms" className="text-spike-blue-light underline" data-testid="auth-terms-link">Terms</Link>{" "}and{" "}
              <Link to="/privacy" className="text-spike-blue-light underline" data-testid="auth-privacy-link">Privacy Policy</Link>.
            </>
          ) : (
            <>Questions about your account? <a href="mailto:hello@spikesports.ca" className="text-spike-blue-light underline">hello@spikesports.ca</a></>
          )}
        </p>
      </div>

      <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} defaultEmail={signin.email} />
    </div>
  );
};

export default Login;
